/**
 * Player - Base class for player characters
 */
import * as THREE from 'three';
import * as SkeletonUtils from 'three/examples/jsm/utils/SkeletonUtils.js';

export class Player {
    constructor(scene, assetManager, options = {}) {
        this.scene = scene;
        this.assetManager = assetManager;

        // Common properties
        this.name = options.name || 'Guest';
        this.health = 100;
        this.maxHealth = 100;
        this.level = 1;
        this.experience = 0;
        this.position = new THREE.Vector3(Math.random() * 300 - 150, 0, Math.random() * 300 - 150);
        this.rotation = new THREE.Euler(0, 0, 0);
        this.modelMesh = null;
        this.modelPath = '';
        this.scale = options.scale || 1;
        this.isDead = false;
        this.isInvincible = false;
        this.invincibilityTimer = null;
        this.spawnPosition = new THREE.Vector3(Math.random() * 300 - 150, 0, Math.random() * 300 - 150);

        // Movement properties
        this.movementSpeed = 5;
        this.rotationSpeed = 2;
        this.moveDirection = { forward: 0, right: 0 };
        this.isMoving = false; // Flag to track if player is moving
        this.isSprinting = false; // Flag to track if player is sprinting

        // Setup inventory with 4 slots
        this.inventory = {
            activeSlot: 0,
            slots: [null, null, null, null]
        };

        // Setup nameplate
        this.nameplate = null;

        // Flag to track if player is initialized
        this.initialized = false;
    }

    /**
     * Initialize the player model and add it to the scene
     * @returns {Promise<boolean>} Whether initialization was successful
     */
    async init() {
        if (!this.scene || !this.assetManager) {
            console.error('Cannot initialize player: missing scene or asset manager');
            return false;
        }

        console.log(`Initializing ${this.constructor.name} player character...`);

        try {
            // Store options reference
            this.options = this.options || {};
            const isRemotePlayer = this.options && this.options.isRemotePlayer;
            
            if (isRemotePlayer) {
                console.log("Initializing as a remote player");
            }
            
            // Load the model
            let model = this.assetManager.getModel(this.modelPath);
            
            if (!model) {
                console.error(`Failed to load player model: ${this.modelPath}`);
                
                // If asset loading is still in progress, we might try again
                if (Object.keys(this.assetManager.models).length === 0) {
                    console.warn('Asset manager appears to still be loading, will create a fallback model');
                }
                
                // Create fallback model
                console.log('Creating fallback model for player');
                const geometry = new THREE.BoxGeometry(1, 2, 1);
                const material = new THREE.MeshStandardMaterial({ 
                    color: this.constructor.name === 'BARBARIAN' ? 0xAA3333 : 0x3333AA 
                });
                this.modelMesh = new THREE.Mesh(geometry, material);
                this.modelMesh.animations = [];
                
                // Add to scene
                this.scene.add(this.modelMesh);
                console.warn('Using fallback model for player');
                
                return false;
            }
            
            console.log(`Model loaded successfully: ${this.modelPath}`);
            
            // Store original animations before cloning
            let originalAnimations = [];
            if (model.animations && model.animations.length > 0) {
                console.log(`Original model has ${model.animations.length} animations`);
                originalAnimations = model.animations;
            } else {
                console.warn('Model does not have animations');
            }
            
            // Clone the model to avoid modifying the original
            this.modelMesh = SkeletonUtils.clone(model);
            
            if (!this.modelMesh) {
                console.error('Failed to clone player model');
                return false;
            }
            
            // Make sure animations are properly copied to the cloned model
            if (originalAnimations.length > 0) {
                console.log(`Copying ${originalAnimations.length} animations to cloned model`);
                // Deep clone the animations to ensure they work properly
                this.modelMesh.animations = originalAnimations.map(anim => anim.clone());
                console.log(`Cloned model now has ${this.modelMesh.animations.length} animations`);
            } else {
                console.warn('Creating fallback placeholder animation');
                // Create a fallback placeholder animation to avoid errors
                this.modelMesh.animations = [
                    new THREE.AnimationClip('idle', 1, [
                        new THREE.NumberKeyframeTrack('.position[y]', [0, 0.5, 1], [0, 0.1, 0])
                    ])
                ];
            }
            
            // Scale the model
            this.modelMesh.scale.set(this.scale, this.scale, this.scale);
            
            // Set initial position
            this.modelMesh.position.copy(this.position);
            
            // Add to scene
            console.log('Adding player model to scene...');
            this.scene.add(this.modelMesh);
            
            // Create nameplate
            this.createNameplate();
            
            // Set up animations
            console.log('Setting up animations...');
            this.setAnimation();
            
            // Set initialized flag
            this.initialized = true;
            console.log(`${this.constructor.name} character initialized successfully`);
            
            return true;
        } catch (error) {
            console.error('Error initializing player:', error);
            return false;
        }
    }

    /**
     * Create a floating nameplate above the player
     */
    createNameplate() {
        // Create a canvas for the nameplate
        const canvas = document.createElement('canvas');
        canvas.width = 256;
        canvas.height = 64;
        const context = canvas.getContext('2d');

        // Draw nameplate background
        context.fillStyle = 'rgba(0, 0, 0, 0.5)';
        context.fillRect(0, 0, canvas.width, canvas.height);

        // Draw player name
        context.font = '24px Arial';
        context.fillStyle = 'white';
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.fillText(this.name, canvas.width / 2, canvas.height / 2);

        // Create texture from canvas
        const texture = new THREE.CanvasTexture(canvas);

        // Create a sprite material with the texture
        const material = new THREE.SpriteMaterial({ map: texture });

        // Create the nameplate sprite
        this.nameplate = new THREE.Sprite(material);
        this.nameplate.scale.set(2, 0.5, 1);
        this.nameplate.position.set(0, 3, 0); // Position above player

        // Add to player model
        if (this.modelMesh) {
            this.modelMesh.add(this.nameplate);
        }
    }

    setAnimation(animationName = null) {
        console.log('Setting up animation system...');
        this.animation = {};

        // Create animation mixer if model exists
        if (!this.modelMesh) {
            console.error('Cannot setup animations: modelMesh is undefined');
            return;
        }

        // Log the available animations
        if (this.modelMesh.animations) {
            console.log(`Model has animations array with ${this.modelMesh.animations.length} animations`);
            this.modelMesh.animations.forEach((anim, index) => {
                console.log(`  Animation ${index}: ${anim.name}`);
            });
        } else {
            console.error('Model has no animations array!');
        }

        // Create animation mixer, ensuring we only create it once
        if (!this.animation.mixer) {
            this.animation.mixer = new THREE.AnimationMixer(this.modelMesh);
        }
        
        // Check if animations are available
        if (!this.modelMesh.animations || this.modelMesh.animations.length === 0) {
            console.warn('No animations found for this model');
            // Create dummy animation actions for fallback
            this.animation.actions = {
                idle: { play: () => {}, stop: () => {}, reset: () => {}, enabled: true },
                run: { play: () => {}, stop: () => {}, reset: () => {}, enabled: true },
                walk: { play: () => {}, stop: () => {}, reset: () => {}, enabled: true },
                attack: { play: () => {}, stop: () => {}, reset: () => {}, enabled: true },
                running: { play: () => {}, stop: () => {}, reset: () => {}, enabled: true },
                walking: { play: () => {}, stop: () => {}, reset: () => {}, enabled: true }
            };
            this.currentAnimation = 'idle';
            return;
        }
        
        console.log(`Found ${this.modelMesh.animations.length} animations`);
        
        // Create actions dictionary only if it doesn't exist yet
        if (!this.animation.actions) {
            this.animation.actions = {};
        }
        
        // Maps animation names to likely indices based on common animations
        const animationIndices = {
            idle: [36, 0, 1], // try these indices for idle
            dancing: [36, 2, 3],
            jumping: [39, 4, 5],
            running: [48, 6, 7],
            run: [48, 6, 7], // alias for running
            running_back: [74, 8, 9],
            walking: [72, 10, 11],
            walk: [72, 10, 11], // alias for walking
            walking_back: [73, 12, 13],
            attack: [15, 16, 17],
            '2H_Melee_Attack_Spinning': [14, 15, 16], // specific barbarian spinning attack animation
            'Spellcast_Shoot': [18, 19, 20], // specific mage shooting spell animation
            '2H_Melee_Attack_Chop': [21, 22, 23], // specific knight chopping attack animation
            '2H_Ranged_Shoot': [24, 25, 26] // specific rogue shooting animation
        };
        
        // Helper to safely get animation by trying multiple potential indices
        const getAnimation = (name, indices) => {
            // First try to find the animation by exact name
            const exactNameMatch = this.modelMesh.animations.find(anim => 
                anim.name === name
            );
            
            if (exactNameMatch) {
                console.log(`Found animation by exact name match: ${exactNameMatch.name}`);
                return this.animation.mixer.clipAction(exactNameMatch);
            }
            
            // For each potential index
            for (const index of indices) {
                // If this index exists in the animations array
                if (index < this.modelMesh.animations.length) {
                    console.log(`Using animation at index ${index} for ${name}`);
                    return this.animation.mixer.clipAction(this.modelMesh.animations[index]);
                }
            }
            
            // If no animation was found by index, try to find by partial name
            const partialMatch = this.modelMesh.animations.find(anim => 
                anim.name.toLowerCase().includes(name.toLowerCase())
            );
            
            if (partialMatch) {
                console.log(`Found animation by partial name match: ${partialMatch.name} for ${name}`);
                return this.animation.mixer.clipAction(partialMatch);
            }
            
            // Fallback: use the first animation as a default
            if (this.modelMesh.animations.length > 0) {
                console.warn(`No animation found for ${name}, using first available animation as fallback`);
                return this.animation.mixer.clipAction(this.modelMesh.animations[0]);
            }
            
            // Create a dummy action as last resort
            console.warn(`No usable animations found for ${name}, using dummy action`);
            return { 
                play: () => {}, 
                stop: () => {}, 
                reset: () => {},
                enabled: true,
                crossFadeFrom: () => {} 
            };
        };
        
        // Initialize common animation actions
        if (!this.animation.actions.idle) this.animation.actions.idle = getAnimation('idle', animationIndices.idle);
        if (!this.animation.actions.run) this.animation.actions.run = getAnimation('run', animationIndices.run);
        if (!this.animation.actions.running) this.animation.actions.running = getAnimation('running', animationIndices.running);
        if (!this.animation.actions.walk) this.animation.actions.walk = getAnimation('walk', animationIndices.walk);
        if (!this.animation.actions.walking) this.animation.actions.walking = getAnimation('walking', animationIndices.walking);
        if (!this.animation.actions.attack) this.animation.actions.attack = getAnimation('attack', animationIndices.attack);
        
        // Special handling for character-specific animations
        this.animation.actions['2H_Melee_Attack_Spinning'] = getAnimation('2H_Melee_Attack_Spinning', animationIndices['2H_Melee_Attack_Spinning']);
        this.animation.actions['Spellcast_Shoot'] = getAnimation('Spellcast_Shoot', animationIndices['Spellcast_Shoot']);
        this.animation.actions['2H_Melee_Attack_Chop'] = getAnimation('2H_Melee_Attack_Chop', animationIndices['2H_Melee_Attack_Chop']);
        this.animation.actions['2H_Ranged_Shoot'] = getAnimation('2H_Ranged_Shoot', animationIndices['2H_Ranged_Shoot']);
        
        // Ensure animations are set correctly
        Object.keys(this.animation.actions).forEach(key => {
            if (this.animation.actions[key]) {
                // Initialize the action with sensible defaults
                this.animation.actions[key].reset();
                this.animation.actions[key].clampWhenFinished = true;
                this.animation.actions[key].setEffectiveTimeScale(1.0);
                this.animation.actions[key].setEffectiveWeight(1.0);
            }
        });
        
        // Set current animation to idle if not set
        if (!this.currentAnimation) {
            this.currentAnimation = 'idle';
        }
        
        // Play the requested animation or idle by default
        this.playAnimation(animationName || 'idle');
    }

    /**
     * Update player state
     * @param {number} deltaTime - Time since last update in seconds
     */
    update(deltaTime) {
        if (!this.initialized) return;

        // Update nameplate position
        if (this.nameplate && this.modelMesh) {
            // Directly update the nameplate position instead of calling update
            const nameplatePosition = this.modelMesh.position.clone().add(new THREE.Vector3(0, 2.4, 0));
            this.nameplate.position.set(0, 3, 0); // Keep relative position to the model mesh
        }

        // Update animation state based on movement, but don't interrupt attack animations
        if (!this._forcePlayAnimation) {
            if (this.isMoving) {
                if (this.isSprinting) {
                    // Running animation
                    this.playAnimation("run");
                } else {
                    // Walking animation
                    this.playAnimation("walk");
                }
            } else {
                // Idle animation
                this.playAnimation("idle");
            }
        }

        // Update animation mixer
        this.updateAnimation(deltaTime * (this.speedAdjustment || 1));
    }

    /**
     * Move player in a direction
     * @param {THREE.Vector3} direction - Direction to move in (normalized)
     * @param {number} deltaTime - Time since last update in seconds
     */
    move(direction, deltaTime) {
        if (!this.initialized || !this.modelMesh) return;

        // Apply movement directly
        this.modelMesh.position.add(direction);
        this.position.copy(this.modelMesh.position);
    }

    /**
     * Rotate player to face a point
     * @param {THREE.Vector3} point - Point to look at
     */
    lookAt(point) {
        if (!this.initialized || !this.modelMesh) return;

        // Create a vector from player to point
        const direction = new THREE.Vector3()
            .subVectors(point, this.modelMesh.position)
            .normalize();

        // Calculate the target rotation
        this.modelMesh.lookAt(point);
        this.rotation.copy(this.modelMesh.rotation);
    }

    /**
     * Apply damage to the player
     * @param {number} amount - Amount of damage to apply
     * @returns {boolean} Whether player is dead after taking damage
     */
    takeDamage(amount) {
        // If already dead or invincible, don't take damage
        if (this.isDead || this.isInvincible) {
            console.log(`Player ${this.name} ${this.isDead ? 'is dead' : 'is invincible'}, ignoring damage: ${amount}`);
            return this.isDead;
        }
        
        this.health = Math.max(0, this.health - amount);
        const isDead = this.health <= 0;
        
        // Handle player death
        if (isDead && this.modelMesh) {
            this.isDead = true;
            
            // Hide the player model when dead
            this.modelMesh.visible = false;
            console.log(`Player died! Health: ${this.health}`);
            
            // Notify game of player death if available
            if (window.game && window.game.onPlayerDeath) {
                window.game.onPlayerDeath(this);
            }
        }
        
        return isDead;
    }

    /**
     * Add experience points
     * @param {number} amount - Amount of XP to add
     */
    addExperience(amount) {
        this.experience += amount;

        // Check for level up
        const nextLevel = this.level + 1;
        const requiredXP = nextLevel * 100; // Simple level up formula

        if (this.experience >= requiredXP) {
            this.levelUp();
        }
    }

    /**
     * Level up the player
     */
    levelUp() {
        this.level++;
        this.maxHealth += 10;
        this.health = this.maxHealth;

        console.log(`${this.name} leveled up to level ${this.level}!`);
    }

    /**
     * Change active inventory slot
     * @param {number} slot - Slot to switch to (0-3)
     */
    changeInventorySlot(slot) {
        if (slot >= 0 && slot < 4) {
            this.inventory.activeSlot = slot;
        }
    }

    /**
     * Clean up resources
     */
    dispose() {
        if (this.modelMesh) {
            this.scene.remove(this.modelMesh);

            // Dispose of geometry and materials
            this.modelMesh.traverse((child) => {
                if (child.isMesh) {
                    if (child.geometry) child.geometry.dispose();
                    if (child.material) {
                        if (Array.isArray(child.material)) {
                            child.material.forEach(material => material.dispose());
                        } else {
                            child.material.dispose();
                        }
                    }
                }
            });

            this.modelMesh = null;
        }

        this.initialized = false;
    }

    // Method to play an animation
    playAnimation(name, crossfadeDuration = 0.3) {
        // If we're in the middle of an attack animation and not transitioning back to idle/walking/running, don't interrupt
        const movementAnims = ['idle', 'walking', 'running', 'walk', 'run'];
        if (this._forcePlayAnimation && 
            this.currentAnimation === '2H_Melee_Attack_Spinning' && 
            !movementAnims.includes(name)) {
            console.log(`Ignoring ${name} animation request during attack`);
            return;
        }
        
        // Cancel any existing animation timers unless we're in forced animation mode
        if (this.animationTimer && !this._forcePlayAnimation) {
            clearTimeout(this.animationTimer);
            this.animationTimer = null;
        }

        // Don't restart the same animation unless it's an attack
        if (name === this.currentAnimation && 
            name !== '2H_Melee_Attack_Spinning' && 
            !name.includes('attack')) {
            return;
        }

        // Normalize animation name using aliases if needed
        let animName = name.toLowerCase();
        console.log(`Anims are as follows Mohsin: ${animName}`);
        const aliases = {
            'idle': ['stand', 'wait', 'idle'],
            'dance': ['dancemoves', 'dancing', 'dance'],
            'jump': ['hop', 'leap', 'jumping'],
            'run': ['jog', 'sprint', 'running', 'walked', 'walk'],
            'attack': ['swing', 'slash', 'strike', '2H_Melee_Attack_Spinning', 'Spellcast_Shoot', '2H_Melee_Attack_Chop', '2H_Ranged_Shoot']
        };

        // Check each alias category
        for (const [key, values] of Object.entries(aliases)) {
            if (values.some(alias => animName.includes(alias.toLowerCase()))) {
                animName = key;
                break;
            }
        }

        // Update the character state
        if (animName === 'run') {
            this.isRunning = true;
        } else {
            this.isRunning = false;
        }

        // Get animation action
        let newAction;

        // If we have animations loaded
        if (this.animation && this.animation.mixer && this.modelMesh) {
            // Special handling for the 2H_Melee_Attack_Spinning animation - use exact name
            if (name === '2H_Melee_Attack_Spinning') {
                try {
                    // Search for this specific animation by name
                    const clip = this.modelMesh.animations.find(anim => anim.name === '2H_Melee_Attack_Spinning');
                    if (clip) {
                        console.log('Found exact animation: 2H_Melee_Attack_Spinning');
                        newAction = this.animation.mixer.clipAction(clip);
                        
                        // Set animation to play once and hold final pose
                        newAction.reset();
                        newAction.setLoop(THREE.LoopOnce);
                        newAction.clampWhenFinished = true;
                        
                        // Use a faster timeScale to ensure animation completes quicker
                        newAction.timeScale = 1.1; // Faster than default (was 0.7) to reduce wait time
                        
                        // Crossfade from current action to this action
                        if (this.currentAnimation && this.animation.actions[this.currentAnimation]) {
                            // Use a shorter crossfade for attack animations
                            const attackCrossfade = 0.1;
                            newAction.setEffectiveWeight(1);
                            newAction.play();
                            this.animation.actions[this.currentAnimation].crossFadeTo(newAction, attackCrossfade, true);
                        } else {
                            newAction.play();
                        }
                        
                        // Store the action for future reference
                        this.animation.actions[name] = newAction;
                        
                        // Always store the animation name (not the action object)
                        this.currentAnimation = name;
                        
                        // Calculate animation duration and add buffer
                        const animationDuration = clip.duration / newAction.timeScale;
                        console.log(`Attack animation duration: ${animationDuration} seconds (with timeScale: ${newAction.timeScale})`);
                        
                        // Don't set a timer to go back to idle - let the attack method handle this
                        
                        return;
                    }
                } catch (error) {
                    console.error('Error playing 2H_Melee_Attack_Spinning:', error);
                }
            }
            
            // Special handling for the Spellcast_Shoot animation - use exact name
            if (name === 'Spellcast_Shoot') {
                try {
                    // Search for this specific animation by name
                    const clip = this.modelMesh.animations.find(anim => anim.name === 'Spellcast_Shoot');
                    if (clip) {
                        console.log('Found exact animation: Spellcast_Shoot');
                        newAction = this.animation.mixer.clipAction(clip);
                        
                        // Set animation to play once and hold final pose
                        newAction.reset();
                        newAction.setLoop(THREE.LoopOnce);
                        newAction.clampWhenFinished = true;
                        
                        // Use a faster timeScale for a quick spell cast
                        newAction.timeScale = 1.3;
                        
                        // Crossfade from current action to this action
                        if (this.currentAnimation && this.animation.actions[this.currentAnimation]) {
                            // Use a shorter crossfade for attack animations
                            const attackCrossfade = 0.1;
                            newAction.setEffectiveWeight(1);
                            newAction.play();
                            this.animation.actions[this.currentAnimation].crossFadeTo(newAction, attackCrossfade, true);
                        } else {
                            newAction.play();
                        }
                        
                        // Store the action for future reference
                        this.animation.actions[name] = newAction;
                        
                        // Always store the animation name (not the action object)
                        this.currentAnimation = name;
                        
                        // Calculate animation duration and add buffer
                        const animationDuration = clip.duration / newAction.timeScale;
                        console.log(`Spell attack animation duration: ${animationDuration} seconds (with timeScale: ${newAction.timeScale})`);
                        
                        // Don't set a timer to go back to idle - let the attack method handle this
                        
                        return;
                    }
                } catch (error) {
                    console.error('Error playing Spellcast_Shoot:', error);
                }
            }
            
            // Special handling for the 2H_Melee_Attack_Chop animation - use exact name
            if (name === '2H_Melee_Attack_Chop') {
                try {
                    // Search for this specific animation by name
                    const clip = this.modelMesh.animations.find(anim => anim.name === '2H_Melee_Attack_Chop');
                    if (clip) {
                        console.log('Found exact animation: 2H_Melee_Attack_Chop');
                        newAction = this.animation.mixer.clipAction(clip);
                        
                        // Set animation to play once and hold final pose
                        newAction.reset();
                        newAction.setLoop(THREE.LoopOnce);
                        newAction.clampWhenFinished = true;
                        
                        // Use a faster timeScale for a quick chop attack
                        newAction.timeScale = 1.2;
                        
                        // Crossfade from current action to this action
                        if (this.currentAnimation && this.animation.actions[this.currentAnimation]) {
                            // Use a shorter crossfade for attack animations
                            const attackCrossfade = 0.1;
                            newAction.setEffectiveWeight(1);
                            newAction.play();
                            this.animation.actions[this.currentAnimation].crossFadeTo(newAction, attackCrossfade, true);
                        } else {
                            newAction.play();
                        }
                        
                        // Store the action for future reference
                        this.animation.actions[name] = newAction;
                        
                        // Always store the animation name (not the action object)
                        this.currentAnimation = name;
                        
                        // Calculate animation duration and add buffer
                        const animationDuration = clip.duration / newAction.timeScale;
                        console.log(`Knight attack animation duration: ${animationDuration} seconds (with timeScale: ${newAction.timeScale})`);
                        
                        // Don't set a timer to go back to idle - let the attack method handle this
                        
                        return;
                    }
                } catch (error) {
                    console.error('Error playing 2H_Melee_Attack_Chop:', error);
                }
            }
            
            // Special handling for the 2H_Ranged_Shoot animation - use exact name
            if (name === '2H_Ranged_Shoot') {
                try {
                    // Search for this specific animation by name
                    const clip = this.modelMesh.animations.find(anim => anim.name === '2H_Ranged_Shoot');
                    if (clip) {
                        console.log('Found exact animation: 2H_Ranged_Shoot');
                        newAction = this.animation.mixer.clipAction(clip);
                        
                        // Set animation to play once and hold final pose
                        newAction.reset();
                        newAction.setLoop(THREE.LoopOnce);
                        newAction.clampWhenFinished = true;
                        
                        // Use appropriate timeScale for a ranged attack
                        newAction.timeScale = 1.1;
                        
                        // Crossfade from current action to this action
                        if (this.currentAnimation && this.animation.actions[this.currentAnimation]) {
                            // Use a shorter crossfade for attack animations
                            const attackCrossfade = 0.1;
                            newAction.setEffectiveWeight(1);
                            newAction.play();
                            this.animation.actions[this.currentAnimation].crossFadeTo(newAction, attackCrossfade, true);
                        } else {
                            newAction.play();
                        }
                        
                        // Store the action for future reference
                        this.animation.actions[name] = newAction;
                        
                        // Always store the animation name (not the action object)
                        this.currentAnimation = name;
                        
                        // Calculate animation duration and add buffer
                        const animationDuration = clip.duration / newAction.timeScale;
                        console.log(`Rogue attack animation duration: ${animationDuration} seconds (with timeScale: ${newAction.timeScale})`);
                        
                        // Don't set a timer to go back to idle - let the attack method handle this
                        
                        return;
                    }
                } catch (error) {
                    console.error('Error playing 2H_Ranged_Shoot:', error);
                }
            }

            // If we're transitioning from attack animation to a movement animation
            if ((this.currentAnimation === '2H_Melee_Attack_Spinning' || 
                 this.currentAnimation === 'Spellcast_Shoot' || 
                 this.currentAnimation === '2H_Melee_Attack_Chop' ||
                 this.currentAnimation === '2H_Ranged_Shoot') && 
                movementAnims.includes(name)) {
                console.log(`Transitioning from attack to ${name}`);
                
                // Reset the existing attack animation if it exists
                if (this.animation.actions['2H_Melee_Attack_Spinning']) {
                    const attackAction = this.animation.actions['2H_Melee_Attack_Spinning'];
                    attackAction.stop();
                    attackAction.reset();
                }
                
                // Reset the Spellcast animation if it exists
                if (this.animation.actions['Spellcast_Shoot']) {
                    const spellcastAction = this.animation.actions['Spellcast_Shoot'];
                    spellcastAction.stop();
                    spellcastAction.reset();
                }
                
                // Reset the Knight chop animation if it exists
                if (this.animation.actions['2H_Melee_Attack_Chop']) {
                    const chopAction = this.animation.actions['2H_Melee_Attack_Chop'];
                    chopAction.stop();
                    chopAction.reset();
                }
                
                // Reset the Rogue shoot animation if it exists
                if (this.animation.actions['2H_Ranged_Shoot']) {
                    const shootAction = this.animation.actions['2H_Ranged_Shoot'];
                    shootAction.stop();
                    shootAction.reset();
                }
            }

            // Handle default case if special handling fails or for other animations
            if (this.animation.actions[name]) {
                newAction = this.animation.actions[name];
                
                // Configure the action based on animation type
                if (name.includes('attack') || animName === 'attack') {
                    // Attack animations play once
                    newAction.setLoop(THREE.LoopOnce);
                    newAction.clampWhenFinished = true;
                    newAction.timeScale = 1.2; // Slightly faster
                } else if (name.includes('jump') || animName === 'jump') {
                    // Jump animations play once
                    newAction.setLoop(THREE.LoopOnce);
                    newAction.clampWhenFinished = false;
                    newAction.timeScale = 1.5; // Faster
                } else {
                    // Other animations loop
                    newAction.setLoop(THREE.LoopRepeat);
                    newAction.clampWhenFinished = false;
                    newAction.timeScale = 1.0; // Normal speed
                }
                
                // Reset and play the new animation
                newAction.reset();
                
                // Crossfade from current animation if it exists
                if (this.currentAnimation && 
                    this.animation.actions[this.currentAnimation] && 
                    this.currentAnimation !== name) {
                    
                    const oldAction = this.animation.actions[this.currentAnimation];
                    
                    // Use appropriate crossfade duration based on animation type
                    let fadeTime = crossfadeDuration;
                    
                    // Shorter crossfade when attacking
                    if (name.includes('attack') || animName === 'attack') {
                        fadeTime = 0.1;
                    }
                    // Longer crossfade when coming from attack to movement
                    else if (this.currentAnimation === '2H_Melee_Attack_Spinning' || 
                             this.currentAnimation.includes('attack')) {
                        fadeTime = 0.2; // Shorter crossfade when exiting attack animations (was 0.5)
                    }
                    
                    // Set weight for smooth transition
                    newAction.setEffectiveWeight(1);
                    newAction.play();
                    oldAction.crossFadeTo(newAction, fadeTime, true);
                } else {
                    newAction.play();
                }
                
                // Store the current animation name
                this.currentAnimation = name;
                
                // Debug output
                console.log(`Playing animation: ${name}`);
            } else {
                console.warn(`Animation "${name}" not found`);
            }
        }
    }

    // Method to update the animation mixer
    updateAnimation(deltaTime) {
        if (!this.animation || !this.animation.mixer) return;
        
        // Ensure non-zero delta time to prevent animations from getting stuck
        if (deltaTime <= 0) {
            deltaTime = 0.016; // Use 16ms as a default (roughly 60fps)
        }
        
        // Clamp delta time to prevent large jumps that cause stuttering
        // This prevents animations from trying to "catch up" after lag
        const clampedDelta = Math.min(deltaTime, 0.1); // Max 100ms
        
        // Handle remote player animations differently to prevent jerky one-frame animations
        if (this.options && this.options.isRemotePlayer) {
            // Initialize animation clock if it doesn't exist
            if (!this.animationClock) {
                this.animationClock = {
                    time: 0,
                    lastUpdate: Date.now() / 1000
                };
            }
            
            // Update animation clock
            const now = Date.now() / 1000;
            const clockDelta = now - this.animationClock.lastUpdate;
            this.animationClock.time += clockDelta;
            this.animationClock.lastUpdate = now;
            
            // If we have a current animation, ensure it's playing correctly
            if (this.currentAnimation && this.animation.actions[this.currentAnimation]) {
                const action = this.animation.actions[this.currentAnimation];
                
                if (action && typeof action.getClip === 'function') {
                    // Get clip duration
                    const clip = action.getClip();
                    if (clip && clip.duration > 0) {
                        // Calculate proper animation time based on our independent clock
                        const animTime = this.animationClock.time % clip.duration;
                        
                        // Directly set animation time if possible
                        if (typeof action.time !== 'undefined') {
                            action.time = animTime;
                        }
                        
                        // Ensure animation is set to loop
                        if (typeof action.setLoop === 'function') {
                            action.setLoop(THREE.LoopRepeat);
                        } else if (action.loop !== undefined) {
                            action.loop = THREE.LoopRepeat;
                        }
                        
                        // Ensure animation is playing
                        if (action.isRunning && !action.isRunning()) {
                            action.play();
                        }
                        
                        // Ensure time scale is normal
                        if (typeof action.setEffectiveTimeScale === 'function') {
                            action.setEffectiveTimeScale(1.0);
                        }
                    }
                }
            }
            
            // Apply multiple small updates for smoother animation
            const smallSteps = 3;
            const smallDelta = clampedDelta / smallSteps;
            
            for (let i = 0; i < smallSteps; i++) {
                this.animation.mixer.update(smallDelta);
            }
        } else {
            // Normal update for local player
            this.animation.mixer.update(clampedDelta);
        }
    }
    
    /**
     * Check if player appears to be in T-pose by examining limb rotations
     * @private
     * @returns {boolean} true if joints appear to be rotated (animation working), false if in T-pose
     */
    _checkForTpose() {
        if (!this.modelMesh) return false;
        
        // Flag to track if we found rotated joints
        let foundRotatedJoints = false;
        
        // Look for bones/joints with non-zero rotations (indicating animation is working)
        this.modelMesh.traverse(object => {
            if (object.isBone || object.type === 'Bone') {
                // Check if bone has rotations that would indicate not in T-pose
                if (object.rotation && 
                    (Math.abs(object.rotation.x) > 0.1 || 
                     Math.abs(object.rotation.z) > 0.1)) {
                    foundRotatedJoints = true;
                }
            }
        });
        
        return foundRotatedJoints;
    }

    /**
     * Set spawn position for respawn
     * @param {THREE.Vector3} position - Position to spawn at
     */
    setSpawnPosition(position) {
        this.spawnPosition.copy(position);
        
        // If player is at default position, also set current position
        if (this.position.equals(new THREE.Vector3(0, 0, 0))) {
            this.position.copy(position);
            if (this.modelMesh) {
                this.modelMesh.position.copy(position);
            }
        }
    }
    
    /**
     * Respawn player after death
     */
    respawn() {
        console.log(`Player ${this.name} respawning...`);
        
        // Reset health and isDead flag
        this.health = this.maxHealth;
        this.isDead = false;
        
        // Show model if it was hidden
        if (this.modelMesh) {
            this.modelMesh.visible = true;
            
            // Move player back to spawn position if available
            if (this.spawnPosition) {
                this.modelMesh.position.copy(this.spawnPosition);
                this.position.copy(this.spawnPosition);
            }
        }
        
        // Make player invincible for 5 seconds
        this.setInvincible(true);
        
        // Add visual effect for invincibility if model exists
        if (this.modelMesh) {
            this._addInvincibilityEffect();
        }
        
        // Notify the game about the respawn
        if (window.game && window.game.onPlayerRespawn) {
            window.game.onPlayerRespawn(this);
        }
        
        console.log(`Player ${this.name} has respawned with health ${this.health} and is invincible for 5 seconds`);
        
        // Update any UI components with the new health
        if (window.uiManager) {
            window.uiManager.updateHealth(this.health, this.maxHealth, false);
        }
    }
    
    /**
     * Set invincibility state
     * @param {boolean} invincible - Whether player should be invincible
     * @param {number} duration - Duration of invincibility in milliseconds (default: 5000ms)
     */
    setInvincible(invincible, duration = 5000) {
        // Clear any existing timer
        if (this.invincibilityTimer) {
            clearTimeout(this.invincibilityTimer);
            this.invincibilityTimer = null;
        }
        
        this.isInvincible = invincible;
        
        // If making invincible, set a timer to turn it off
        if (invincible && duration > 0) {
            console.log(`Player ${this.name} is now invincible for ${duration/1000} seconds`);
            
            this.invincibilityTimer = setTimeout(() => {
                this.isInvincible = false;
                console.log(`Player ${this.name} is no longer invincible`);
                
                // Remove invincibility effect
                if (this.modelMesh && this.invincibilityEffect) {
                    this._removeInvincibilityEffect();
                }
                
            }, duration);
        }
    }
    
    /**
     * Add a visual effect to show player is invincible
     * @private
     */
    _addInvincibilityEffect() {
        // Remove any existing effect first
        this._removeInvincibilityEffect();
        
        // Create a pulsing glow effect
        this.invincibilityEffect = {
            originalMaterials: [],
            newMaterials: [],
            pulseAnimation: null
        };
        
        // Store original materials and create glowing versions
        this.modelMesh.traverse(child => {
            if (child.isMesh && child.material) {
                // Store reference to original material
                if (Array.isArray(child.material)) {
                    this.invincibilityEffect.originalMaterials.push({
                        mesh: child,
                        materials: [...child.material]
                    });
                    
                    // Create glowing versions of each material
                    const glowingMaterials = child.material.map(mat => {
                        const newMat = mat.clone();
                        newMat.emissive = new THREE.Color(0x3399ff);
                        newMat.emissiveIntensity = 0.5;
                        return newMat;
                    });
                    
                    child.material = glowingMaterials;
                    this.invincibilityEffect.newMaterials.push(glowingMaterials);
                } else {
                    this.invincibilityEffect.originalMaterials.push({
                        mesh: child,
                        material: child.material
                    });
                    
                    // Create glowing version of material
                    const newMat = child.material.clone();
                    newMat.emissive = new THREE.Color(0x3399ff);
                    newMat.emissiveIntensity = 0.5;
                    child.material = newMat;
                    this.invincibilityEffect.newMaterials.push(newMat);
                }
            }
        });
        
        // Create pulsing animation
        let pulseIntensity = 0.5;
        let pulseDirection = 0.01;
        
        this.invincibilityEffect.pulseAnimation = setInterval(() => {
            pulseIntensity += pulseDirection;
            
            // Reverse direction at min/max
            if (pulseIntensity >= 0.8 || pulseIntensity <= 0.2) {
                pulseDirection *= -1;
            }
            
            // Update all materials
            this.modelMesh.traverse(child => {
                if (child.isMesh && child.material) {
                    if (Array.isArray(child.material)) {
                        child.material.forEach(mat => {
                            if (mat.emissive) {
                                mat.emissiveIntensity = pulseIntensity;
                            }
                        });
                    } else if (child.material.emissive) {
                        child.material.emissiveIntensity = pulseIntensity;
                    }
                }
            });
        }, 50);
    }
    
    /**
     * Remove invincibility visual effect
     * @private
     */
    _removeInvincibilityEffect() {
        if (!this.invincibilityEffect) return;
        
        // Stop animation
        if (this.invincibilityEffect.pulseAnimation) {
            clearInterval(this.invincibilityEffect.pulseAnimation);
        }
        
        // Restore original materials
        this.invincibilityEffect.originalMaterials.forEach(item => {
            if (item.mesh) {
                item.mesh.material = Array.isArray(item.materials) ? item.materials : item.material;
            }
        });
        
        // Clean up new materials to prevent memory leaks
        this.invincibilityEffect.newMaterials.forEach(material => {
            if (Array.isArray(material)) {
                material.forEach(mat => mat.dispose());
            } else if (material) {
                material.dispose();
            }
        });
        
        this.invincibilityEffect = null;
    }
}

/**
 * BARBARIAN - Medieval warrior character class
 */
export class BARBARIAN extends Player {
    constructor(scene, assetManager, options = {}) {
        super(scene, assetManager, options);

        // Override base properties
        this.modelPath = 'characters.barbarian';
        this.health = 100;
        this.maxHealth = 100;
        this.movementSpeed = 6; // Faster but less health
        this.scale = 1.0;

        // Default weapon (already attached to model)
        this.weaponType = 'sword';
    }

    async init() {
        const success = await super.init();

        if (success) {
            // Hide extra weapons
            this.modelMesh.traverse((child) => {
                if(child.name == "Barbarian_Round_Shield" || child.name == "Mug" || child.name == "1H_Axe" || child.name == "1H_Axe_Offhand"){
                    child.visible = false
                }
            });

            return true;
        }

        return false;
    }

    /**
     * Perform a melee attack (BARBARIAN specific)
     * @returns {Object} Attack data (damage, range)
     */
    attack() {
        // Prevent multiple attack animations from overlapping
        if (this.isAttacking) {
            console.log('Already attacking, ignoring additional attack request');
            return null;
        }
        
        // Set flag to prevent attack spam
        this.isAttacking = true;
        console.log('Starting attack animation');
        
        // Force animation system to prioritize the attack animation
        this._forcePlayAnimation = true;
        
        // Play the special spinning attack animation for Barbarian
        this.playAnimation('2H_Melee_Attack_Spinning');
        
        // Display visual feedback
        console.log('⚔️ BARBARIAN ATTACK! ⚔️');
        
        // Create a visual indicator that the attack happened
        if (this.scene) {
            // Create a flash effect in the scene
            const flashGeometry = new THREE.SphereGeometry(1.5, 16, 16);
            const flashMaterial = new THREE.MeshBasicMaterial({ 
                color: 0xff0000, 
                transparent: true, 
                opacity: 0.5
            });
            const flash = new THREE.Mesh(flashGeometry, flashMaterial);
            
            // Position the flash at the player's position
            flash.position.copy(this.position);
            flash.position.y += 1; // Raise it to player's center
            
            // Add to scene
            this.scene.add(flash);
            
            // Animate the flash
            const startTime = performance.now();
            const animate = () => {
                const elapsed = performance.now() - startTime;
                const progress = Math.min(elapsed / 500, 1); // 500ms animation
                
                // Scale up and fade out
                const scale = 1 + progress * 2;
                flash.scale.set(scale, scale, scale);
                flashMaterial.opacity = 0.5 * (1 - progress);
                
                if (progress < 1) {
                    requestAnimationFrame(animate);
                } else {
                    this.scene.remove(flash);
                    // Dispose resources
                    flashGeometry.dispose();
                    flashMaterial.dispose();
                }
            };
            
            requestAnimationFrame(animate);
        }
        
        // Make sure to reset the attacking flag after animation finishes
        // Use a shorter duration to ensure faster transition
        const attackDuration = 1; // seconds - reduced from 2.5s for faster transition
        setTimeout(() => {
            this.isAttacking = false;
            this._forcePlayAnimation = false;
            console.log('Attack complete - ready for next attack');
            
            // Force transition back to appropriate animation based on movement state
            if (this.isMoving) {
                if (this.isSprinting) {
                    this.playAnimation("run");
                } else {
                    this.playAnimation("walk");
                }
            } else {
                this.playAnimation("idle");
            }
            
            // If the animation action still exists, make sure to reset it
            if (this.animation && this.animation.actions && this.animation.actions['2H_Melee_Attack_Spinning']) {
                const attackAction = this.animation.actions['2H_Melee_Attack_Spinning'];
                if (attackAction.reset) {
                    attackAction.reset();
                }
                if (attackAction.stop) {
                    attackAction.stop();
                }
            }
        }, attackDuration * 1000);
        
        // BARBARIAN has standardized melee attacks (25 damage)
        return {
            damage: 25,
            range: 2,
            type: 'melee'
        };
    }
}

/**
 * KNIGHT - Modern tactical character class
 */
export class KNIGHT extends Player {
    constructor(scene, assetManager, options = {}) {
        super(scene, assetManager, options);

        // Override base properties
        this.modelPath = 'characters.knight';
        this.health = 100;
        this.maxHealth = 100;
        this.movementSpeed = 6; // Faster but less health
        this.scale = 1.0;

        // Default weapon (already attached to model)
        this.weaponType = 'sword';
    }

    async init() {
        const success = await super.init();

        if (success) {
            // Hide extra weapons
            this.modelMesh.traverse((child) => {
                if(child.name == "Badge_Shield" || child.name == "Rectangle_Shield" || child.name == "Round_Shield" || child.name == "Spike_Shield" || child.name =="1H_Sword" || child.name == "1H_Sword_Offhand"){
                    child.visible = false
                }
            });

            return true;
        }

        return false;
    }

    /**
     * Perform a ranged attack (KNIGHT specific)
     * @returns {Object} Attack data (damage, range)
     */
    attack() {
        // Prevent multiple attack animations from overlapping
        if (this.isAttacking) {
            console.log('Already attacking, ignoring additional attack request');
            return null;
        }
        
        // Set flag to prevent attack spam
        this.isAttacking = true;
        console.log('Starting Knight attack animation');
        
        // Force animation system to prioritize the attack animation
        this._forcePlayAnimation = true;
        
        // Play the chopping attack animation for Knight
        this.playAnimation('2H_Melee_Attack_Chop');
        
        // Display visual feedback
        console.log('⚔️ KNIGHT ATTACK! ⚔️');
        
        // Create a visual indicator that the attack happened
        if (this.scene) {
            // Create a flash effect in the scene
            const flashGeometry = new THREE.SphereGeometry(1.4, 16, 16);
            const flashMaterial = new THREE.MeshBasicMaterial({ 
                color: 0xffcc00, 
                transparent: true, 
                opacity: 0.6
            });
            const flash = new THREE.Mesh(flashGeometry, flashMaterial);
            
            // Position the flash at the player's position
            flash.position.copy(this.position);
            flash.position.y += 1.2; // Raise it to player's center
            
            // Add to scene
            this.scene.add(flash);
            
            // Animate the flash
            const startTime = performance.now();
            const animate = () => {
                const elapsed = performance.now() - startTime;
                const progress = Math.min(elapsed / 450, 1); // 450ms animation
                
                // Scale up and fade out
                const scale = 1 + progress * 2;
                flash.scale.set(scale, scale, scale);
                flashMaterial.opacity = 0.6 * (1 - progress);
                
                if (progress < 1) {
                    requestAnimationFrame(animate);
                } else {
                    this.scene.remove(flash);
                    // Dispose resources
                    flashGeometry.dispose();
                    flashMaterial.dispose();
                }
            };
            
            requestAnimationFrame(animate);
        }
        
        // Make sure to reset the attacking flag after animation finishes
        const attackDuration = 1.15; // seconds - increased by 0.25s to match longer animation
        setTimeout(() => {
            this.isAttacking = false;
            this._forcePlayAnimation = false;
            console.log('Knight attack complete - ready for next attack');
            
            // Force transition back to appropriate animation based on movement state
            if (this.isMoving) {
                if (this.isSprinting) {
                    this.playAnimation("run");
                } else {
                    this.playAnimation("walk");
                }
            } else {
                this.playAnimation("idle");
            }
            
            // If the animation action still exists, make sure to reset it
            if (this.animation && this.animation.actions && this.animation.actions['2H_Melee_Attack_Chop']) {
                const chopAction = this.animation.actions['2H_Melee_Attack_Chop'];
                if (chopAction.reset) {
                    chopAction.reset();
                }
                if (chopAction.stop) {
                    chopAction.stop();
                }
            }
        }, attackDuration * 1000);
        
        // KNIGHT has standardized melee attacks (25 damage)
        return {
            damage: 25,
            range: 3,
            type: 'melee'
        };
    }
}

/**
 * MAGE - Modern tactical character class
 */
export class MAGE extends Player {
    constructor(scene, assetManager, options = {}) {
        super(scene, assetManager, options);

        // Override base properties
        this.modelPath = 'characters.mage';
        this.health = 100;
        this.maxHealth = 100;
        this.movementSpeed = 4; // Slower but more health
        this.scale = 1.2;

        // Default weapon (already attached to model)
        this.weaponType = 'staff';
    }

    async init() {
        const success = await super.init();

        if (success) {
            // Hide extra weapons
            this.modelMesh.traverse((child) => {
                if(child.name == "1H_Wand" || child.name == "Spellbook"|| child.name == "Spellbook_open"){
                    child.visible = false
                }
            });

            return true;
        }

        return false;
    }

    /**
     * Perform a ranged attack (MAGE specific)
     * @returns {Object} Attack data (damage, range)
     */
    attack() {
        // Prevent multiple attack animations from overlapping
        if (this.isAttacking) {
            console.log('Already attacking, ignoring additional attack request');
            return null;
        }
        
        // Set flag to prevent attack spam
        this.isAttacking = true;
        console.log('Starting Mage attack animation');
        
        // Force animation system to prioritize the attack animation
        this._forcePlayAnimation = true;
        
        // Play the spellcast shoot animation for Mage
        this.playAnimation('Spellcast_Shoot');
        
        // Display visual feedback
        console.log('🔮 MAGE ATTACK! 🔮');
        
        // Create a visual indicator that the attack happened
        if (this.scene) {
            // Create a magical flash effect in the scene
            const flashGeometry = new THREE.SphereGeometry(1.2, 16, 16);
            const flashMaterial = new THREE.MeshBasicMaterial({ 
                color: 0x00ffff, 
                transparent: true, 
                opacity: 0.7
            });
            const flash = new THREE.Mesh(flashGeometry, flashMaterial);
            
            // Position the flash at the player's position
            flash.position.copy(this.position);
            flash.position.y += 1.5; // Raise it to player's center
            
            // Add to scene
            this.scene.add(flash);
            
            // Animate the flash
            const startTime = performance.now();
            const animate = () => {
                const elapsed = performance.now() - startTime;
                const progress = Math.min(elapsed / 400, 1); // 400ms animation
                
                // Scale up and fade out
                const scale = 1 + progress * 2;
                flash.scale.set(scale, scale, scale);
                flashMaterial.opacity = 0.7 * (1 - progress);
                
                if (progress < 1) {
                    requestAnimationFrame(animate);
                } else {
                    this.scene.remove(flash);
                    // Dispose resources
                    flashGeometry.dispose();
                    flashMaterial.dispose();
                }
            };
            
            requestAnimationFrame(animate);
        }
        
        // Make sure to reset the attacking flag after animation finishes
        const attackDuration = 0.8; // seconds
        setTimeout(() => {
            this.isAttacking = false;
            this._forcePlayAnimation = false;
            console.log('Mage attack complete - ready for next attack');
            
            // Force transition back to appropriate animation based on movement state
            if (this.isMoving) {
                if (this.isSprinting) {
                    this.playAnimation("run");
                } else {
                    this.playAnimation("walk");
                }
            } else {
                this.playAnimation("idle");
            }
            
            // If the animation action still exists, make sure to reset it
            if (this.animation && this.animation.actions && this.animation.actions['Spellcast_Shoot']) {
                const spellcastAction = this.animation.actions['Spellcast_Shoot'];
                if (spellcastAction.reset) {
                    spellcastAction.reset();
                }
                if (spellcastAction.stop) {
                    spellcastAction.stop();
                }
            }
        }, attackDuration * 1000);
        
        // Return attack data
        return {
            damage: 25,
            range: 20,
            type: 'ranged'
        };
    }
}

/**
 * ROGUE - Modern tactical character class
 */
export class ROGUE extends Player {
    constructor(scene, assetManager, options = {}) {
        super(scene, assetManager, options);

        // Override base properties
        this.modelPath = 'characters.rogue';
        this.health = 100;
        this.maxHealth = 100;
        this.movementSpeed = 3; // Medium and medium health
        this.scale = 1.2;

        // Default weapon (already attached to model)
        this.weaponType = 'crossbow';
    }

    async init() {
        const success = await super.init();

        if (success) {
            // Hide extra weapons
            this.modelMesh.traverse((child) => {
                if(child.name == "1H_Crossbow" || child.name == "Knife" || child.name == "Knife_Offhand" || child.name == "Throwable"){
                    child.visible = false
                }
            });

            return true;
        }

        return false;
    }

    /**
     * Perform a ranged attack (ROGUE specific)
     * @returns {Object} Attack data (damage, range)
     */
    attack() {
        // Prevent multiple attack animations from overlapping
        if (this.isAttacking) {
            console.log('Already attacking, ignoring additional attack request');
            return null;
        }
        
        // Set flag to prevent attack spam
        this.isAttacking = true;
        console.log('Starting Rogue attack animation');
        
        // Force animation system to prioritize the attack animation
        this._forcePlayAnimation = true;
        
        // Play the ranged shoot animation for Rogue
        this.playAnimation('2H_Ranged_Shoot');
        
        // Display visual feedback
        console.log('🏹 ROGUE ATTACK! 🏹');
        
        // Create a visual indicator that the attack happened
        if (this.scene) {
            // Create a visual effect in the scene
            const flashGeometry = new THREE.SphereGeometry(1.0, 16, 16);
            const flashMaterial = new THREE.MeshBasicMaterial({ 
                color: 0x33cc33, 
                transparent: true, 
                opacity: 0.6
            });
            const flash = new THREE.Mesh(flashGeometry, flashMaterial);
            
            // Position the flash at the player's position
            flash.position.copy(this.position);
            flash.position.y += 1.3; // Raise it to player's center
            
            // Add to scene
            this.scene.add(flash);
            
            // Animate the flash
            const startTime = performance.now();
            const animate = () => {
                const elapsed = performance.now() - startTime;
                const progress = Math.min(elapsed / 350, 1); // 350ms animation
                
                // Scale up and fade out
                const scale = 1 + progress * 3;
                flash.scale.set(scale, scale, scale);
                flashMaterial.opacity = 0.6 * (1 - progress);
                
                if (progress < 1) {
                    requestAnimationFrame(animate);
                } else {
                    this.scene.remove(flash);
                    // Dispose resources
                    flashGeometry.dispose();
                    flashMaterial.dispose();
                }
            };
            
            requestAnimationFrame(animate);
        }
        
        // Make sure to reset the attacking flag after animation finishes
        const attackDuration = 0.9; // seconds
        setTimeout(() => {
            this.isAttacking = false;
            this._forcePlayAnimation = false;
            console.log('Rogue attack complete - ready for next attack');
            
            // Force transition back to appropriate animation based on movement state
            if (this.isMoving) {
                if (this.isSprinting) {
                    this.playAnimation("run");
                } else {
                    this.playAnimation("walk");
                }
            } else {
                this.playAnimation("idle");
            }
            
            // If the animation action still exists, make sure to reset it
            if (this.animation && this.animation.actions && this.animation.actions['2H_Ranged_Shoot']) {
                const shootAction = this.animation.actions['2H_Ranged_Shoot'];
                if (shootAction.reset) {
                    shootAction.reset();
                }
                if (shootAction.stop) {
                    shootAction.stop();
                }
            }
        }, attackDuration * 1000);
        
        // ROGUE has standardized ranged attacks (25 damage)
        return {
            damage: 25,
            range: 25,
            type: 'ranged'
        };
    }
} 